import { useCallback, useState } from "react";
import publicApi from "@/lib/publicApi";
import { FlightSearchResponse } from "@/types/search/flight";

export interface ShareableLinkMetadata {
  uid: string;
  type: string;
  status: string;
  openCount: number;
  expiresAt: string;
  tripType: "oneway" | "roundtrip" | "multicity";
  metadata: unknown[];
  searchQuery: Record<string, unknown>;
}

export function usePublicFlightShare(shareId: string) {
  const [metadata, setMetadata] = useState<ShareableLinkMetadata | null>(null);
  const [flightData, setFlightData] = useState<FlightSearchResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);

  const loadShare = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setIsExpired(false);

    try {
      const metaResponse = await publicApi.get<ShareableLinkMetadata>(
        `/flight/shareable-links/${shareId}`,
      );
      setMetadata(metaResponse.data);

      const searchResponse = await publicApi.post<FlightSearchResponse>(
        `/flight/shareable-links/${shareId}/search`,
      );
      setFlightData(searchResponse.data);
    } catch (err: any) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to load shared flights";

      if (status === 410) {
        setIsExpired(true);
        setError("This shareable link has expired.");
      } else if (status === 404) {
        setError("Shareable link not found.");
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [shareId]);

  return {
    metadata,
    flightData,
    isLoading,
    error,
    isExpired,
    loadShare,
  };
}
